/**
 * Action Detail Dialog
 *
 * Shows full details of an agent action with execute/dismiss buttons.
 *
 * Date: 2026-02-18
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Info, ExternalLink, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  executeAction,
  dismissAction,
  getPriorityColor,
  type AgentAction,
} from '@/services/agentActionService';
import { useToast } from '@/components/ui/use-toast';

interface ActionDetailDialogProps {
  action: AgentAction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActionComplete: () => void;
}

export const ActionDetailDialog: React.FC<ActionDetailDialogProps> = ({
  action,
  open,
  onOpenChange,
  onActionComplete,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!action) return null;

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const handleExecute = async () => {
    setIsExecuting(true);
    try {
      const result = await executeAction(action.id);
      if (result.data.executionResult?.success === false) {
        toast({
          title: 'Execution Failed',
          description: typeof result.data.executionResult.error === 'string'
            ? `Action failed: ${result.data.executionResult.error}`
            : 'Action failed',
          variant: 'destructive',
        });
      } else toast({
        title: 'Action Executed',
        description:
          typeof result.data.executionResult?.message === 'string'
            ? result.data.executionResult.message
            : 'Action was executed successfully',
      });
      onActionComplete();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Execution Failed',
        description: error instanceof Error ? error.message : 'Failed to execute action',
        variant: 'destructive',
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      await dismissAction(action.id, dismissReason || undefined);
      toast({
        title: 'Action Dismissed',
        description: 'Action was dismissed successfully',
      });
      setShowDismissConfirm(false);
      onActionComplete();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Dismissal Failed',
        description: error instanceof Error ? error.message : 'Failed to dismiss action',
        variant: 'destructive',
      });
    } finally {
      setIsDismissing(false);
    }
  };

  const getPriorityIcon = () => {
    if (action.priority === 'critical' || action.priority === 'high') {
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
    return <Info className="h-5 w-5 text-muted-foreground" />;
  };

  const getStatusBadge = () => {
    switch (action.status) {
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      case 'executed':
        if (action.execution_result?.success === false) {
          return <Badge variant="destructive">Failed</Badge>;
        }
        return <Badge className="bg-green-500">Executed</Badge>;
      case 'dismissed':
        return <Badge variant="secondary">Dismissed</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      default:
        return null;
    }
  };

  const formatKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const renderPayloadValue = (key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">Not set</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-muted-foreground italic">None</span>;
      }
      return (
        <div className="space-y-2 mt-1">
          {value.map((item, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
                {idx + 1}
              </span>
              <span className="text-sm pt-0.5">
                {typeof item === 'object' ? JSON.stringify(item) : String(item)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-muted/50 p-2 rounded overflow-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    if (typeof value === 'boolean') {
      return value ? (
        <Badge variant="outline" className="bg-green-50 text-green-700">Yes</Badge>
      ) : (
        <Badge variant="outline" className="bg-red-50 text-red-700">No</Badge>
      );
    }

    return <span className="text-sm">{String(value)}</span>;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 pr-6">
              {getPriorityIcon()}
              <DialogTitle className="flex-1 truncate">{action.title}</DialogTitle>
              <Badge className={`text-white flex-shrink-0 ${getPriorityColor(action.priority)}`}>
                {action.priority}
              </Badge>
            </div>
            <DialogDescription>
              Suggested by <strong>{action.agent_name}</strong> on {formatDate(action.created_at)}
              {action.site_name && (
                <>
                  {' '}for site <strong>{action.site_name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Description */}
            {action.description && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                <p className="mt-1 whitespace-pre-wrap break-words">{action.description}</p>
              </div>
            )}

            {/* Status and metadata */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="font-medium mr-2">Status:</span>
                {getStatusBadge()}
              </div>
              <div>
                <span className="font-medium mr-2">Action Type:</span>
                <code className="bg-muted px-2 py-0.5 rounded text-xs">{action.action_type}</code>
              </div>
              {action.source_type && (() => {
                // Check for linkable quality issue - support multiple source types
                const isQualitySource = ['quality_issue', 'quality_audit_analysis'].includes(action.source_type);
                const payloadIssueId = action.action_payload?.issue_id;
                const issueId = action.source_id || (typeof payloadIssueId === 'string' ? payloadIssueId : null);
                // Only link if issueId looks like a UUID (not a pattern name)
                const isValidUuid = issueId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(issueId);

                return (
                  <div className="flex items-center">
                    <span className="font-medium mr-2">Source:</span>
                    {isQualitySource && isValidUuid ? (
                      <Button
                        variant="link"
                        className="h-auto p-0 text-primary"
                        onClick={() => {
                          onOpenChange(false);
                          navigate(`/quality-audit?issueId=${issueId}`);
                        }}
                      >
                        Quality Issue
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    ) : (
                      <span>{action.source_type}</span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Action payload - structured display */}
            {Object.keys(action.action_payload).length > 0 && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Action Details</Label>
                <div className="mt-2 space-y-3 p-3 bg-muted/50 rounded-md">
                  {Object.entries(action.action_payload).map(([key, value]) => (
                    <div key={key} className="border-b border-border/50 pb-2 last:border-0 last:pb-0">
                      <div className="text-xs font-medium text-muted-foreground mb-1">
                        {formatKey(key)}
                      </div>
                      <div>{renderPayloadValue(key, value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Execution result (if already executed) */}
            {action.status === 'executed' && action.execution_result && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Execution Result</Label>
                <pre
                  role={action.execution_result.success === false ? 'alert' : undefined}
                  className={`mt-1 p-3 rounded-md text-xs overflow-auto max-h-40 ${
                    action.execution_result.success === false
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : 'bg-green-50 dark:bg-green-900/20'
                  }`}
                >
                  {JSON.stringify(action.execution_result, null, 2)}
                </pre>
                {action.executed_by_email && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Executed by {action.executed_by_email} on {formatDate(action.executed_at)}
                  </p>
                )}
              </div>
            )}

            {/* Dismissal info (if dismissed) */}
            {action.status === 'dismissed' && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Dismissal Info</Label>
                <div className="mt-1 p-3 bg-muted rounded-md text-sm">
                  {action.dismissal_reason || 'No reason provided'}
                </div>
                {action.dismissed_by_email && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Dismissed by {action.dismissed_by_email} on {formatDate(action.dismissed_at)}
                  </p>
                )}
              </div>
            )}

            {/* Expiry warning */}
            {action.expires_at && action.status === 'pending' && (
              <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <AlertCircle className="h-4 w-4" />
                <span>Expires on {formatDate(action.expires_at)}</span>
              </div>
            )}
          </div>

          {action.status === 'pending' && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowDismissConfirm(true)}
                disabled={isExecuting}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Dismiss
              </Button>
              <Button onClick={handleExecute} disabled={isExecuting}>
                {isExecuting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                {action.suggested_action_label || 'Execute Action'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Dismiss confirmation dialog */}
      <AlertDialog open={showDismissConfirm} onOpenChange={setShowDismissConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss Action?</AlertDialogTitle>
            <AlertDialogDescription>
              This will dismiss the action without executing it. You can optionally provide a reason.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="dismiss-reason">Reason (optional)</Label>
            <Textarea
              id="dismiss-reason"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              placeholder="Why are you dismissing this action?"
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDismissing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDismiss} disabled={isDismissing}>
              {isDismissing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Dismiss
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
